import React, { useState } from 'react'
import { Button, Input, Typography } from '@fullscreendigital/carrefour-dex-fe'
import { useMemo } from 'react'
import { getInitialValues, getValidationSchema } from './helpers'
import { useFormik } from 'formik'
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { useNotification } from '../../../../../../../hooks'
import { TitleHeader } from '../../../../Common/TitleHeader'
import { CircularProgress, Container, Grid, Paper } from '@mui/material'
import { makeCoreGetRequest, makeCorePutRequest } from "../../../../../../../utils/api"
import { generatePath, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useQuery } from 'react-query'
import ImageUpload from '../../../../Common/ImageUpload/ImageUpload'
import { URLS } from '../../../../constants'

type DSOrderDTO = {
  id: number
  orderNumber: number
  supplierCode: number
  supplierName: string
  orderStatus: number
  orderStatusDescription: string
  shopCode: string
  rayonName: string
  dsDeliveryStatus: any
  dsDeliveryStatusValue: string
  orderDate: string | null
  expectedDeliveryDate: string | null
  deliveryDate: string | null
  deliveryHour: string | null
  temperatureCompliant: boolean | null
}

type ThermoSelect = 'true' | 'false' | 'none'

const toThermoSelect = (compl: boolean | null): ThermoSelect =>
  compl === null ? 'none' : compl ? 'true' : 'false'

const toCompliance = (v: ThermoSelect): boolean | null =>
  v === 'none' ? null : v === 'true'

const OrderDetails = () => {
  const [hasThermodiagramImage, setHasThermodiagramImage] = useState(false)
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  let { id } = useParams()
  const [sp] = useSearchParams()
  const supplierCode = sp.get('supplierCode')

  const validationSchema = useMemo(() => getValidationSchema(), [])
  const initialValues = useMemo(() => getInitialValues(), [])

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        const compliance = toCompliance(values.thermodiagramConform)

        const payload = {
          temperatureCompliant: compliance,
          noticeNumber: values.noticeNumber,
          arrivalTemperature:
            values.thermodiagramConform === 'false' && values.temperature != null
              ? Number(values.temperature)
              : null
        }

        await makeCorePutRequest(`/shop-reception-mobile/ds-order/accept/${id}/${supplierCode}`, payload)
        navigate(generatePath(URLS.AUTH_SUPPLIERS_GOODS_VALIDATION, { id }) + `?supplierCode=${supplierCode}`)
      } catch (error) {
        showNotification({
          message: `Error while submitting. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  })

  const { data: order, isLoading } = useQuery<DSOrderDTO>(
    ['ds-order', id],
    () => makeCoreGetRequest(`/shop-reception-mobile/ds-order/${id}/${supplierCode}`, null),
    {
      onSuccess: (data) => {
        formik.setValues(v => ({
          ...v,
          noticeNumber: v.noticeNumber || '',
          thermodiagramConform: toThermoSelect(data.temperatureCompliant),
          temperature: null,

          orderStatus: data.orderStatus,
          dsDeliveryStatus: data.dsDeliveryStatus,
          dsDeliveryStatusValue: data.dsDeliveryStatusValue,
          deliveryDate: data.deliveryDate,
          deliveryHour: data.deliveryHour
        }))
      },
      onError: error => {
        showNotification({
          message: `Error while fetching order. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const needsTemp = formik.values.thermodiagramConform === 'false'
  const tempMissing = needsTemp && (formik.values.temperature === null || Number.isNaN(Number(formik.values.temperature)))
  const hasNotice = typeof formik.values.noticeNumber === 'number' && formik.values.noticeNumber > 0
  const approveDisabled = !(
    (formik.values.thermodiagramConform === 'none' || hasThermodiagramImage) &&
    hasNotice &&
    !tempMissing
  )

  if (isLoading || formik.isSubmitting) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  const handleThermodiagramUploadSuccess = () => {
    setHasThermodiagramImage(true)
    showNotification({ message: 'Imaginea a fost încărcată cu succes.', type: 'success' })
  }

  const handleThermodiagramUploadError = (error: any) => {
    showNotification({ message: `Error uploading image: ${error.message}`, type: 'error' })
  }

  const fmt = (d?: string | null) => (d ? d : '—')

  return (
    <Container>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TitleHeader title="DETALII COMANDA" />
        </Grid>

        {order && (
          <Grid item xs={12}>
            <Paper style={{ padding: 16 }}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="medium" component="p">
                    <strong>Comandă:</strong> {order.orderNumber}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="medium" component="p">
                    <strong>Magazin:</strong> {order.shopCode} &nbsp;|&nbsp;
                    <strong>Raion:</strong> {order.rayonName || '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="medium" component="p">
                    <strong>Furnizor:</strong> {order.supplierName} ({order.supplierCode})
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="medium" component="p">
                    <strong>Dată comandă:</strong> {fmt(order.orderDate)} &nbsp;|&nbsp;
                    <strong>Livrare estimată:</strong> {fmt(order.expectedDeliveryDate)} &nbsp;|&nbsp;
                    <strong>Livrat la:</strong> {fmt(order.deliveryDate)} &nbsp;|&nbsp;
                    <strong>Oră livrare:</strong> {fmt(order.deliveryHour)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="medium" component="p">
                    <strong>Conformitate temperatură:</strong> {order.temperatureCompliant === null ? '—' : order.temperatureCompliant ? 'DA' : 'NU'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography variant="medium" component="div" style={{ margin: '12px 0' }}>
            <Input
              type="number"
              variant="outlined"
              label="Numar Aviz"
              onChange={e => {
                const raw = (e.target as HTMLInputElement).value
                formik.setFieldValue('noticeNumber', raw === '' ? null : Number(raw))
              }}
              value={formik.values.noticeNumber ?? ''}
              name="noticeNumber"
              error={!!formik.errors.noticeNumber}
              helperText={formik.errors.noticeNumber as string}
              width="100%"
            />
          </Typography>
        </Grid>

        <Grid item xs={12} style={{ position: 'relative' }}>
          <Grid container>
            <Grid item xs={12}>
              <Input
                type="select"
                label="Conformitate temperatura marfa"
                variant="standard"
                width="80%"
                value={formik.values.thermodiagramConform}
                values={[
                  { value: 'true', label: 'DA' },
                  { value: 'false', label: 'NU' },
                  { value: 'none', label: 'NU E CAZUL' }
                ]}
                onChange={e => formik.setFieldValue('thermodiagramConform', (e.target as HTMLInputElement).value)}
                error={!!formik.errors.thermodiagramConform}
                helperText={formik.errors.thermodiagramConform as string}
              />
            </Grid>
            {formik.values.thermodiagramConform !== 'none' && (
                <Grid item xs={12} style={{ marginTop: '24px' }}>
                  <ImageUpload
                    uploadUrl={`/shop-reception-mobile/ds-order/upload-temperature-picture/${id}/${order.supplierCode}`}
                    onSuccess={handleThermodiagramUploadSuccess}
                    onError={handleThermodiagramUploadError}
                    btnStyle={{ position: 'absolute', right: 0, top: '42px' }}
                  />
                </Grid>
              )}
          </Grid>
        </Grid>

        {formik.values.thermodiagramConform === 'false' && (
          <Grid item xs={12}>
            <Input
              type="number"
              label="Temperatură"
              variant="standard"
              width="100%"
              value={formik.values.temperature ?? ''}
              onChange={event =>
                formik.setFieldValue('temperature', (event.target as HTMLInputElement).value)
              }
              error={!!formik.errors.temperature}
              helperText={!!formik.errors.temperature}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            label="Aproba comanda"
            style={{ width: '100%' }}
            onClick={() => !approveDisabled && formik.handleSubmit()}
            disabled={approveDisabled || formik.isSubmitting}
          />
        </Grid>
      </Grid>
    </Container>
  )
}

export default OrderDetails
